import { z } from "zod";
import type { StateCreator } from "zustand";
import type { Store } from "../globalStore";
import { HrtExtended } from "./procedureSlice";

/**
 * Originated from ChargeName field in the checkout service
 */
const PaymentType = z.enum(["CASH", "INSURED"]);

const priceData = z.object({
  price: z.number(),
  pricedTreatmentId: z.number(),
  metadata: z.record(z.unknown()).optional(),
});

export type PaymentTypeType = z.infer<typeof PaymentType>;
export type priceDataType = z.infer<typeof priceData>;

export type PricingError = {
  errorText: string;
  errorBlurb: string;
  isSecondaryInsuranceFailure?: boolean; // true when primary succeeds but secondary fails
} | null;

export type PricingStatus = "SUCCESS" | "WAITING" | "NEUTRAL" | "ERROR";
export type Mode = "CASH" | "INSURED";

export type PricingMapEntries = {
  [key: number]: {
    mode: Mode;
    priceData: {
      [key in PaymentTypeType]: priceDataType | undefined;
    };
  };
};

export type InNetworkProviderIdsByPractice = Record<number, number[]>;
export type InNetworkProviderIdsByHrt = Record<
  number,
  InNetworkProviderIdsByPractice
>;

const CartItem = z.object({
  id: z.number(),
  paymentType: z.enum([
    "INSURED",
    "CASH",
    "SELFPAYDISCOUNTED",
    "OVERRIDE_INSURED",
    "OVERRIDE_SELFPAY",
  ]),
  price: z.number(),
  hrts: z.array(HrtExtended),
  pricedTreatmentId: z.number(),
  cashOverride: z.boolean().optional(),
  discountCodeApplied: z.string().optional(),
  originalPrice: z.number().optional(),
  ehrPrimaryAppointmentTypeId: z.string().optional(),
  metadata: z.any(),
});

export type CartItem = z.infer<typeof CartItem>;

/**
 * @type isInsuranceValidated whether or not someone has completed the insured data intake flow ( when the connecting animation dissapears )
 */
export type PricingMapSlice = {
  isInsuranceValidated: boolean;
  isFetchingInsuredPrices: boolean;
  pricingMapEntries: PricingMapEntries;
  inNetworkProviderIdsByHrt: InNetworkProviderIdsByHrt;
  pricingError: PricingError; // also includes network errors
  pricingStatus: PricingStatus;
  resetForm: (() => void) | null;
  stepperInitialStep: number;
  isAddingSecondaryInsurance: boolean;
  resetPricingMap: () => void;
  setResetForm: (resetFn: () => void) => void;
  setPricingStatus: (props: PricingStatus) => void;
  setPricingError: (pricingErrors: PricingError) => void;
  setIsInsuranceValidated: (arg: boolean) => void;
  setIsFetchingInsuredPrices: (newValue: boolean) => void;
  setPricingMap: (pricingMap: PricingMapEntries) => void;
  setInNetworkProviderIdsByHrt: (arg: InNetworkProviderIdsByHrt) => void;
  resetInNetworkProviderIdsByHrt: () => void;
  setStepperInitialStep: (step: number) => void;
  setIsAddingSecondaryInsurance: (value: boolean) => void;
  initiateAddPrimaryInsurance: () => void;
  initiateAddSecondaryInsurance: () => void;
};

export const pricingMapInitialState = {
  isInsuranceValidated: false,
  isFetchingInsuredPrices: false,
  pricingStatus: "NEUTRAL" as const,
  pricingMapEntries: {},
  inNetworkProviderIdsByHrt: {},
  pricingError: null,
  resetForm: null,
  stepperInitialStep: 1,
  isAddingSecondaryInsurance: false,
};

/**
 * Pricing map store is used to create a different price strong structure than the category > product > hrts so
 * hrt prices can be retrieved immediately in a flattened object. Used for insurance prices only.
 * @param set
 * @param get
 * @returns
 */
export const createPricingMapSlice: StateCreator<
  Store,
  [["zustand/devtools", never], ["zustand/persist", unknown]],
  [],
  PricingMapSlice
> = (set, get) => ({
  ...pricingMapInitialState,
  setPricingStatus: (pricingStatus: PricingStatus) => {
    set({ pricingStatus });
  },
  setResetForm: (resetFn) => set({ resetForm: resetFn }),
  setPricingError: (pricingError: PricingError) => {
    set({ pricingError });
  },
  resetPricingMap: () => {
    set({
      pricingMapEntries: {},
      inNetworkProviderIdsByHrt: {},
    });
  },
  setPricingMap: (pricingMap: PricingMapEntries) => {
    set({
      pricingMapEntries: {
        ...get().pricingMapEntries,
        ...pricingMap,
      },
    });
  },
  setInNetworkProviderIdsByHrt: (inNetworkProviderIdsByHrt) => {
    set({ inNetworkProviderIdsByHrt });
  },
  resetInNetworkProviderIdsByHrt: () => {
    set({ inNetworkProviderIdsByHrt: {} });
  },
  setIsInsuranceValidated: (status: boolean) => {
    set({ isInsuranceValidated: status });
  },
  setIsFetchingInsuredPrices: (newValue: boolean) => {
    set({ isFetchingInsuredPrices: newValue });
  },
  setStepperInitialStep: (step: number) => {
    set({ stepperInitialStep: step });
  },
  setIsAddingSecondaryInsurance: (value: boolean) => {
    set({ isAddingSecondaryInsurance: value });
  },
  initiateAddPrimaryInsurance: () => {
    set({
      isAddingSecondaryInsurance: false,
      stepperInitialStep: 5,
      pricingStatus: "NEUTRAL",
      pricingError: null,
      lastInsuranceCheckResults: null,
    });
  },
  initiateAddSecondaryInsurance: () => {
    set({
      isAddingSecondaryInsurance: true,
      stepperInitialStep: 9,
      pricingStatus: "NEUTRAL",
      pricingError: null,
      lastInsuranceCheckResults: null,
    });
  },
});
